<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { useClipboard } from "@vueuse/core";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog-v2";
import { Code, KeyRound } from "lucide-vue-next";
import CodeBlock from "@/components/CodeBlock.vue";

const props = defineProps<{
    token: string;
}>();

// using 2 different clipboard instances to avaid duplicated copy label
const clipboard1 = useClipboard();
const clipboard2 = useClipboard();

const code = `
<body>
    <div id="zaptime-container"></div>

    <${"script"} src="https://iframe.zaptime.app/zaptime.js"></${"script"}>

    <${"script"}>
        Zaptime({config: {token: '${props.token}' }})
            .render('#zaptime-container')
    </${"script"}>
</body>
`;

function copyCode() {
    clipboard1.copy(code);
}

function copyToken() {
    clipboard2.copy(props.token);
}
</script>

<template>
    <Dialog>
        <DialogTrigger as-child>
            <slot />
        </DialogTrigger>
        <DialogContent class="sm:max-w-[760px]">
            <DialogHeader>
                <DialogTitle>{{ $t("integration_code.title") }}</DialogTitle>

                <DialogDescription>
                    {{ $t("integration_code.description") }}
                    <a
                        class="font-medium text-z-primary-600"
                        href="https://napoveda.zaptime.cz/jak-pridat-zaptime-na-vas-web.html"
                        target="_blank"
                        >{{ $t("integration_code.documentation") }}.</a
                    >
                </DialogDescription>

                <template #icon>
                    <Code class="text-z-primary-600" />
                </template>
            </DialogHeader>
            <div class="px-2 sm:max-w-[600px] md:max-w-[740px] max-w-[350px]">
                <CodeBlock :code="code"></CodeBlock>
            </div>

            <div
                v-if="clipboard1.isSupported"
                class="mt-4 gap-3 px-4 flex justify-center"
            >
                <Button
                    class="gap-2"
                    type="button"
                    variant="outline"
                    @click="copyCode"
                >
                    <Code :size="16" />
                    {{
                        clipboard1.copied.value
                            ? $t("copied")
                            : $t("integration_code.copy_code")
                    }}
                </Button>
                <Button
                    class="gap-2"
                    type="button"
                    variant="outline"
                    @click="copyToken"
                >
                    <KeyRound :size="16" />
                    {{
                        clipboard2.copied.value
                            ? $t("copied")
                            : $t("integration_code.copy_token")
                    }}
                </Button>
            </div>
            <div v-else class="text-sm">Missing permissions to copy code</div>
            <DialogFooter>
                <div class="flex w-full justify-end">
                    <DialogClose as-child>
                        <Button type="button" variant="outline">
                            {{ $t("close") }}
                        </Button>
                    </DialogClose>
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
